/*
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { DraggableCore } from 'react-draggable';
import classnames from 'classnames';
import findIndex from 'lodash/findIndex';
import debounce from 'lodash/debounce';
import Arrow from 'components/Icons/Arrow/Arrow';
import useInview from 'utils/inview';
import useKeys from 'utils/keys';
import nextTick from 'utils/nextTick';
import './SectionMenu.scss';

/**
 * This is the SectionMenu component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function SectionMenu({ section, items }) {
  const container = useRef(null);
  const track = useRef(null);
  const inview = useInview(container, {
    threshold: 0.2,
  });
  const [ready, setReady] = useState(false);
  const [sizes, setSizes] = useState([]);
  const [bounds, setBounds] = useState({});
  const [dragging, setDragging] = useState({
    active: false,
    start: 0,
  });
  const [position, setPosition] = useState(0);
  const [navigate, setNavigate] = useState(false);
  const [active, setActive] = useState();
  const [disabled, setDisabled] = useState(true);
  const [transition, setTransition] = useState(false);

  useEffect(() => {
    if (items.length) {
      const active = findIndex(items, ({ active }) => active);
      setActive(active);
    }
  }, [items]);

  useEffect(() => {
    function calculateSizes() {
      nextTick(() => {
        const nodes = track.current.querySelectorAll('.item');

        const sizes = Array.from(nodes).map(node => {
          return node.offsetWidth;
        });

        const overflow =
          track.current.offsetWidth - container.current.offsetWidth;

        const disabled = overflow < 20;

        setBounds({
          right: 0,
          left: -(track.current.offsetWidth - sizes[sizes.length - 1]),
        });
        setSizes([...sizes]);
        setDisabled(disabled);
        if (disabled) {
          setReady(true);
        }
      });
    }

    const onResize = debounce(calculateSizes, 200);

    if (items.length) {
      calculateSizes();
    }

    window.addEventListener('resize', onResize);
    return function cleanup() {
      window.removeEventListener('resize', onResize);
    };
  }, [items]);

  useState(() => {
    setTransition(true);
  }, [navigate]);

  useEffect(() => {
    if (!disabled) {
      const x = sizes.reduce((accumulator, currentValue, index) => {
        if (index < active) {
          return accumulator - currentValue;
        }
        return accumulator;
      }, 0);

      setPosition(x);
    } else {
      setPosition(0);
    }
    setNavigate(true);
  }, [sizes, active, disabled]);

  useEffect(() => {
    let timeout = null;
    timeout = setTimeout(() => {
      setReady(true);
    }, 400);

    return function cleanup() {
      clearTimeout(timeout);
    };
  }, [position]);

  useKeys('section-menu', !disabled && inview, {
    left: goPrev,
    right: goNext,
  });

  function getInview() {
    const nodes = track.current.querySelectorAll('.item');

    const index = findIndex(nodes, node => {
      const pos = node.getBoundingClientRect();
      const width = node.offsetWidth / 2;
      return width + pos.x >= 0;
    });

    return index < 0 ? items.length : index;
  }

  function goPrev() {
    if (navigate) {
      const prev = active - 1;
      if (prev >= 0) {
        setNavigate(false);
        setActive(prev);
      }
    }
  }

  function goNext() {
    if (navigate) {
      const next = active + 1;
      if (next < items.length) {
        setNavigate(false);
        setActive(next);
      }
    }
  }

  function goTo(index) {
    if (navigate) {
      setNavigate(false);
      setActive(index);
    }
  }

  function onNavigationEnd({ target }) {
    if (target === track.current) {
      if (!navigate) {
        setTransition(false);
      }
    }
  }

  function renderNav() {
    if (disabled) return null;

    return (
      <nav>
        <button
          className={classnames({ disabled: active === 0 })}
          onClick={goPrev}
        >
          <Arrow left />
        </button>
        <button
          className={classnames({ disabled: active === items.length - 1 })}
          onClick={goNext}
        >
          <Arrow right />
        </button>
      </nav>
    );
  }

  function renderItems() {
    return items.map(({ id, url, title, active }) => (
      <a
        key={id}
        href={url}
        className={classnames('item', { active })}
        onClick={e => {
          if (!disabled) {
            if (dragging.dir !== 0) {
              e.preventDefault();
            }
          }
        }}
      >
        {title}
      </a>
    ));
  }

  return (
    <div
      className={classnames('section-menu', {
        'animate-visible': inview,
      })}
    >
      <div className="page-padding top animate-1">
        <span>{section}</span>
        {renderNav()}
      </div>
      <div className="page-padding items-container">
        <div
          ref={container}
          className={classnames('items', {
            'animate-fade-in': ready,
          })}
        >
          <DraggableCore
            axis="x"
            bounds={bounds}
            disabled={disabled || !navigate}
            onStart={(e, { x }) => {
              e.preventDefault();
              setDragging(props => ({
                ...props,
                start: x,
                active: true,
                dir: 0,
              }));
            }}
            onDrag={(e, { deltaX, x }) => {
              e.preventDefault();
              const newPos = position + deltaX;
              if (newPos >= bounds.left && newPos < bounds.right) {
                const distance = dragging.start - x;
                setPosition(newPos);
                setDragging(props => ({
                  ...props,
                  dir:
                    Math.abs(distance) < 10 ? 0 : distance / Math.abs(distance),
                }));
              }
            }}
            onStop={e => {
              setDragging(props => ({
                ...props,
                active: false,
              }));

              const next = getInview();
              if (next === active) {
                if (dragging.dir !== 0) {
                  e.preventDefault();
                  if (dragging.dir > 0) {
                    goNext();
                  } else {
                    goPrev();
                  }
                }
              } else {
                e.preventDefault();
                goTo(next);
              }
            }}
          >
            <div
              ref={track}
              className={classnames('track', {
                'with-transition': transition,
                ready,
                dragging: dragging.active,
              })}
              style={{
                transform: `translateX(${position}px)`,
                touchAction: 'pan-y',
              }}
              onTransitionEnd={onNavigationEnd}
            >
              {renderItems()}
            </div>
          </DraggableCore>
        </div>
      </div>
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
SectionMenu.propTypes = {
  section: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      active: PropTypes.bool.isRequired,
    })
  ),
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
SectionMenu.defaultProps = {
  items: [],
};

export default SectionMenu;
